import { useEffect, useRef, useState } from "react";
import {
  X, Mic, Square, Play, Pause, Trash2, Pencil, Loader2, Check, MessageSquare, Clock, AlertTriangle,
} from "lucide-react";
import * as offline from "@/lib/offline";
import { feedbackAudioUrl } from "@/lib/listenApi";
import { fmtTime } from "./listenShared";

const MAX_SECONDS = 120;

function StatusChip({ status, synced }) {
  if (status === "n/a") return null;
  const map = {
    queued: { label: synced ? "Transcribing…" : "Will transcribe when online", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
    transcribing: { label: "Transcribing…", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
    ready: { label: "Transcribed", cls: "text-teal-300 border-teal-400/30 bg-teal-400/10" },
    failed: { label: "Transcription unavailable", cls: "text-slate-400 border-white/10 bg-white/5" },
  };
  const m = map[status] || map.queued;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] ${m.cls}`}>{m.label}</span>;
}

function NoteRow({ note, token, onDiscard, onEdit }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note.text || "");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <div data-testid={`note-row-${note.feedback_id}`} className="rounded-2xl border border-white/8 bg-white/[0.03] p-3.5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-[11px] text-teal-300/80">
          <Clock className="h-3 w-3" /> {fmtTime(note.position)}
        </span>
        <div className="flex items-center gap-2">
          <StatusChip status={note.transcript_status} synced={note.synced} />
          {note.kind === "text" && !editing && (
            <button data-testid={`note-edit-${note.feedback_id}`} onClick={() => { setEditing(true); setVal(note.text || ""); }} className="text-slate-500 hover:text-slate-200">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <button data-testid={`note-discard-${note.feedback_id}`} onClick={() => onDiscard(note.feedback_id)} className="text-slate-500 hover:text-rose-300">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {note.passage_label && (
        <div className="mt-1 truncate font-mono text-[10px] uppercase tracking-wider text-slate-500">{note.passage_label}</div>
      )}

      {note.kind === "voice" && (
        <div className="mt-2 flex items-center gap-2">
          <button
            data-testid={`note-play-${note.feedback_id}`}
            onClick={togglePlay}
            disabled={!note.synced && !note.blob}
            className="grid h-9 w-9 place-items-center rounded-full bg-teal-400 text-slate-950 disabled:opacity-30"
          >
            {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="ml-0.5 h-4 w-4" fill="currentColor" />}
          </button>
          <span className="text-xs text-slate-400">Voice note</span>
          <audio
            ref={audioRef}
            src={note.blob ? URL.createObjectURL(note.blob) : (note.synced ? feedbackAudioUrl(note.feedback_id, token) : undefined)}
            onEnded={() => setPlaying(false)}
            className="hidden"
          />
        </div>
      )}

      {editing ? (
        <div className="mt-2">
          <textarea
            data-testid={`note-edit-input-${note.feedback_id}`}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-xl border border-white/10 bg-[#0b0f15] p-2.5 text-sm text-slate-100 outline-none focus:border-teal-500/50"
          />
          <div className="mt-2 flex gap-2">
            <button data-testid={`note-edit-save-${note.feedback_id}`} onClick={async () => { await onEdit(note.feedback_id, val); setEditing(false); }} className="rounded-full bg-teal-400 px-4 py-1.5 text-xs font-semibold text-slate-950">Save</button>
            <button onClick={() => setEditing(false)} className="rounded-full border border-white/10 px-4 py-1.5 text-xs text-slate-300">Cancel</button>
          </div>
        </div>
      ) : (
        (note.text || note.kind === "text") && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-200">
            {note.text || <span className="text-slate-500">No transcript yet.</span>}
          </p>
        )
      )}
    </div>
  );
}

export function FeedbackSheet({ open, onClose, token, item, position, preview, onChanged }) {
  const [tab, setTab] = useState("text");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [recState, setRecState] = useState("idle"); // idle|recording|preview|error
  const [elapsed, setElapsed] = useState(0);
  const [recBlob, setRecBlob] = useState(null);
  const [recError, setRecError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewUrl = recBlob ? URL.createObjectURL(recBlob) : null;

  const refresh = async () => {
    if (!item) return;
    const all = await offline.feedbackForItem(item.item_id);
    setNotes(all);
  };

  useEffect(() => { if (open) { setTab("text"); setText(""); setRecState("idle"); setRecBlob(null); setElapsed(0); refresh(); } }, [open, item?.item_id]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (!open) return null;

  const afterChange = async () => { await refresh(); onChanged && onChanged(); };

  const saveText = async () => {
    const t = text.trim();
    if (!t || preview) { if (preview) onClose(); return; }
    setSaving(true);
    const id = offline.newFeedbackId();
    await offline.queueTextFeedback({ feedbackId: id, itemId: item.item_id, itemTitle: item.title, position, text: t });
    if (navigator.onLine) await offline.flushFeedback(token);
    setText("");
    setSaving(false);
    await afterChange();
  };

  const startRec = async () => {
    setRecError("");
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setRecState("error"); setRecError("Recording isn’t supported on this device. You can type a note instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((tk) => tk.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecBlob(blob);
        setRecState("preview");
      };
      mediaRef.current = mr;
      mr.start();
      setRecState("recording"); setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= MAX_SECONDS) { stopRec(); return MAX_SECONDS; }
          return e + 1;
        });
      }, 1000);
    } catch {
      setRecState("error");
      setRecError("Microphone access was blocked. Allow it in your browser settings, or type a note instead.");
    }
  };

  const stopRec = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { mediaRef.current && mediaRef.current.state !== "inactive" && mediaRef.current.stop(); } catch { /* ignore */ }
  };

  const saveVoice = async () => {
    if (!recBlob || preview) { if (preview) onClose(); return; }
    setSaving(true);
    const id = offline.newFeedbackId();
    await offline.queueVoiceFeedback({ feedbackId: id, itemId: item.item_id, itemTitle: item.title, position, blob: recBlob, contentType: recBlob.type });
    if (navigator.onLine) await offline.flushFeedback(token);
    setRecBlob(null); setRecState("idle"); setSaving(false);
    await afterChange();
  };

  const discard = async (fid) => { await offline.discardFeedback(fid, token); await afterChange(); };
  const editNote = async (fid, val) => { if (val.trim()) { await offline.editTextFeedback(fid, val.trim(), token); await afterChange(); } };

  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-end bg-black/60 backdrop-blur-sm" data-testid="feedback-sheet">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0" />
      <div className="relative max-h-[85%] overflow-y-auto rounded-t-3xl border-t border-white/10 bg-[#0b0f15] px-5 pb-8 pt-5">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-50">Leave a note</h3>
          <button data-testid="feedback-close" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[11px] text-teal-300/80">
          <Clock className="h-3 w-3" /> at {fmtTime(position)} · {item?.title}
        </p>

        {preview && (
          <div className="mt-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
            Preview mode — notes are not saved.
          </div>
        )}

        <div className="mt-4 flex gap-2 rounded-full bg-white/5 p-1">
          <button data-testid="feedback-tab-text" onClick={() => setTab("text")} className={`flex-1 rounded-full py-2 text-sm ${tab === "text" ? "bg-teal-400 text-slate-950" : "text-slate-300"}`}>Type</button>
          <button data-testid="feedback-tab-voice" onClick={() => setTab("voice")} className={`flex-1 rounded-full py-2 text-sm ${tab === "voice" ? "bg-teal-400 text-slate-950" : "text-slate-300"}`}>Record</button>
        </div>

        {tab === "text" ? (
          <div className="mt-4">
            <textarea
              data-testid="feedback-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="What stood out here? A word, a line, a feeling…"
              className="w-full resize-none rounded-2xl border border-white/10 bg-[#070a0f] p-3.5 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-teal-500/50"
            />
            <button data-testid="feedback-save-text" onClick={saveText} disabled={saving || !text.trim()} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-teal-400 py-3 text-sm font-semibold text-slate-950 disabled:opacity-30">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save note
            </button>
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center">
            <p className="mb-3 text-center text-xs text-slate-500">
              Your microphone is used only while recording, and the note stays private to the author. Up to 2 minutes.
            </p>
            {recState === "idle" && (
              <button data-testid="feedback-record-start" onClick={startRec} className="grid h-20 w-20 place-items-center rounded-full bg-rose-500/90 text-white hover:bg-rose-500">
                <Mic className="h-8 w-8" />
              </button>
            )}
            {recState === "recording" && (
              <>
                <button data-testid="feedback-record-stop" onClick={stopRec} className="grid h-20 w-20 place-items-center rounded-full bg-rose-500 text-white ring-4 ring-rose-500/30">
                  <Square className="h-7 w-7" fill="currentColor" />
                </button>
                <div className="mt-3 font-mono text-sm text-rose-300">{fmtTime(elapsed)} / 2:00</div>
              </>
            )}
            {recState === "preview" && previewUrl && (
              <div className="w-full">
                <audio data-testid="feedback-record-preview" src={previewUrl} controls className="w-full" />
                <div className="mt-3 flex gap-2">
                  <button data-testid="feedback-save-voice" onClick={saveVoice} disabled={saving} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-teal-400 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save note
                  </button>
                  <button data-testid="feedback-rerecord" onClick={() => { setRecBlob(null); setRecState("idle"); }} className="rounded-full border border-white/10 px-4 py-3 text-sm text-slate-300">Re-record</button>
                </div>
              </div>
            )}
            {recState === "error" && (
              <div className="flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {recError}
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
            <MessageSquare className="h-3.5 w-3.5" /> Your notes for this section
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-slate-600">No notes here yet.</p>
          ) : (
            <div className="space-y-2.5" data-testid="notes-list">
              {notes.map((n) => (
                <NoteRow key={n.feedback_id} note={n} token={token} onDiscard={discard} onEdit={editNote} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
